import type { ImportBankAccountsResponse } from "@/generated/backend/v1/bank_link";
import { AccountType } from "@/generated/core/v1/accounts";
import { LoanType } from "@/generated/core/v1/debts";
import {
  type AccountKindKey,
  INVESTMENT_KINDS,
  newAccount,
  newProperty,
  type OnboardingDraft,
} from "@/plan-logic/questionnaire/draft";

/**
 * A Flinks connection's accounts in the onboarding draft (POST /api/bank-link/import). The backend has
 * already chosen each account's kind; this only places it, for the owner picked before connecting:
 * deposit and investment accounts and credit cards with the accounts, other loans with the debts, and
 * a mortgage on a property. New items take the uuid the backend chose (`entityUuid`), so the saved
 * family's entities are the ones its daily reads update. An item already imported (same bank account
 * id) gets the new balance and keeps everything the user changed. The user checks it all on the
 * following screens.
 */
export type FlinksPatch = Pick<
  OnboardingDraft,
  | "accounts"
  | "debts"
  | "hasDebts"
  | "properties"
  | "hasProperty"
  | "noInvestments"
  | "flinksConnections"
>;

const ACCOUNT_KIND: Partial<Record<AccountType, AccountKindKey>> = {
  [AccountType.CHEQUING_ACCOUNT]: "chequing",
  [AccountType.RRSP_ACCOUNT]: "rrsp",
  [AccountType.TFSA_ACCOUNT]: "tfsa",
  [AccountType.FHSA_ACCOUNT]: "fhsa",
  [AccountType.RESP_ACCOUNT]: "resp",
  [AccountType.NON_REGISTERED_INVESTMENT_ACCOUNT]: "nonreg",
};

export function applyFlinksImport(
  draft: OnboardingDraft,
  res: ImportBankAccountsResponse,
  ownerUuid: string,
): FlinksPatch {
  // The backend keeps the connection: reconnecting the same login returns the same id.
  const connectionId = res.connectionId;
  const existing = draft.flinksConnections.find((c) => c.id === connectionId);
  const accounts = [...draft.accounts];
  const debts = [...draft.debts];
  const properties = [...draft.properties];
  const skipped = [...res.skipped];

  for (const a of res.accounts) {
    const ref = { connectionId, accountId: a.externalId };
    const balance = a.balance;
    if (a.kind.oneofKind === "mortgage") {
      let i = properties.findIndex((p) => p.flinks?.accountId === ref.accountId);
      if (i >= 0) {
        const created = properties[i].flinks?.created ?? false;
        properties[i] = { ...properties[i], owing: balance, flinks: { ...ref, created } };
        continue;
      }
      // Otherwise fill a mortgage the user started without a balance, before adding a property. The
      // mortgage takes the backend's uuid either way, so later balances find it.
      i = properties.findIndex((p) => !p.flinks && p.hasMortgage && p.owing === undefined);
      if (i >= 0) {
        properties[i] = {
          ...properties[i],
          mortgageUuid: a.entityUuid,
          owing: balance,
          flinks: { ...ref, created: false },
        };
      } else {
        properties.push({
          ...newProperty(properties.length === 0),
          mortgageUuid: a.entityUuid,
          hasMortgage: true,
          owing: balance,
          flinks: { ...ref, created: true },
        });
      }
      continue;
    }
    if (a.kind.oneofKind === "loanType" && a.kind.loanType !== LoanType.CREDIT_CARD) {
      const i = debts.findIndex((d) => d.flinks?.accountId === ref.accountId);
      if (i >= 0) debts[i] = { ...debts[i], balance, flinks: ref };
      else
        debts.push({
          uuid: a.entityUuid,
          kind: a.kind.loanType,
          name: a.name,
          memberUuid: ownerUuid,
          balance,
          creditLimit: a.creditLimit,
          flinks: ref,
        });
      continue;
    }
    let kind: AccountKindKey | undefined =
      a.kind.oneofKind === "loanType"
        ? "credit"
        : a.kind.oneofKind === "accountType"
          ? ACCOUNT_KIND[a.kind.accountType]
          : undefined;
    if (kind === "chequing" && a.savings) kind = "savings";
    if (!kind) {
      skipped.push({ name: a.name, reason: "Add this kind of account after your plan is built." });
      continue;
    }
    const i = accounts.findIndex((x) => x.flinks?.accountId === ref.accountId);
    if (i >= 0) accounts[i] = { ...accounts[i], balance, flinks: ref };
    else
      accounts.push({
        ...newAccount(kind, ownerUuid),
        uuid: a.entityUuid,
        name: a.name,
        balance,
        flinks: ref,
      });
  }

  const connection = { id: connectionId, institution: res.institution, ownerUuid, skipped };
  return {
    accounts,
    debts,
    hasDebts: draft.hasDebts || debts.length > 0,
    properties,
    hasProperty: draft.hasProperty || properties.length > 0,
    noInvestments: draft.noInvestments && !accounts.some((a) => INVESTMENT_KINDS.includes(a.kind)),
    flinksConnections: existing
      ? draft.flinksConnections.map((c) => (c.id === connectionId ? connection : c))
      : [...draft.flinksConnections, connection],
  };
}

/** "Remove": the connection and what it filled in (a mortgage it filled on the user's own property
 * goes back to blank). */
export function removeFromDraft(draft: OnboardingDraft, connectionId: string): FlinksPatch {
  const mine = (ref?: { connectionId: string }) => ref?.connectionId === connectionId;
  const debts = draft.debts.filter((d) => !mine(d.flinks));
  const properties = draft.properties
    .filter((p) => !(mine(p.flinks) && p.flinks!.created))
    .map((p) => (mine(p.flinks) ? { ...p, owing: undefined, flinks: undefined } : p));
  return {
    accounts: draft.accounts.filter((a) => !mine(a.flinks)),
    debts,
    hasDebts: draft.hasDebts && debts.length > 0,
    properties,
    hasProperty: draft.hasProperty && properties.length > 0,
    noInvestments: draft.noInvestments,
    flinksConnections: draft.flinksConnections.filter((c) => c.id !== connectionId),
  };
}
